import { IncorporationRequestDataType } from '@commenda/types';
import { LegacyDocumentType } from '@commenda/prisma';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { Country, State } from 'country-state-city';
import { DocumentSummaryField, SummaryFieldKS } from '../common';
import { getDirectorName } from '../Director/common';

const getRoles = (
  director: DirectorBase,
  corporationId: number,
  shareholderDirectorIds: Set<number>
) => {
  const allRoles = director?.corporationHasDirector
    ?.filter((dir) => dir.corporationId === corporationId)
    ?.flatMap((dir) => {
      const roles = [];
      const info = dir.additionalDirectorInformation;

      if (info?.isDirector) roles.push('Director');
      if (shareholderDirectorIds.has(director.id)) roles.push('Shareholder');

      return roles;
    });

  return Array.from(new Set(allRoles)).filter(Boolean).join(', ');
};

const SGDirectorSummaryField = ({
  director,
  data,
  corporationId,
  shareholderDirectorIds,
}: {
  corporationId: number;
  director: DirectorBase;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  shareholderDirectorIds: Set<number>;
}) => {
  const directorVsEquityMap: Record<
    number,
    { sharePercentage: number; name: string; id: number }
  > = {};

  const openFile = async (fileId: number) => {
    const res = await getSignedUrlByFileId(fileId, null);
    window.open(res);
  };

  data?.directors?.forEach((dir) => {
    const details = dir.corporationHasDirector.find(
      (directorWithCorporation) =>
        directorWithCorporation.corporationId === data.corporationId
    )?.additionalDirectorInformation;

    directorVsEquityMap[dir.id] = {
      name: getDirectorName(dir),
      sharePercentage: details?.sharePercentage,
      id: dir.id,
    };
  });

  const phoneNumber = director?.number;
  const email = director?.email;
  const currentAddress = director?.operatingAddress;
  const sharePercentage = directorVsEquityMap[director.id]?.sharePercentage;
  const nationality = director?.personalDetails?.nationality;
  const dateOfBirth = director?.dateOfBirth;
  const countryOfResidence = director?.personalDetails?.residence;

  const passportFiles =
    director?.directorFiles?.filter(
      (file) => file.documentType === LegacyDocumentType.Passport
    ) ?? [];

  const passportFile =
    passportFiles.length > 0 ? passportFiles[passportFiles.length - 1] : null;

  const governmentIdFile = director?.directorFiles?.find(
    (file) => file.documentType === LegacyDocumentType.GovernmentId
  );

  return (
    <div className="p-6 flex flex-col gap-2 bg-bg-disabled-subtle">
      <SummaryFieldKS 
        heading="First Name" 
        value={director?.directorFirstName || '-'} 
      />
      <SummaryFieldKS 
        heading="Last Name" 
        value={director?.directorLastName || '-'} 
      />
      
      {sharePercentage && sharePercentage !== 0 ? (
        <SummaryFieldKS
          heading="Ownership percentage"
          value={`${sharePercentage}%`}
        />
      ) : null}

      <SummaryFieldKS
        heading="Role"
        value={getRoles(director, corporationId, shareholderDirectorIds)}
      />

      {phoneNumber && <SummaryFieldKS heading="Phone" value={phoneNumber} />}
      {email && <SummaryFieldKS heading="Email" value={email} />}

      {dateOfBirth ? (
        <SummaryFieldKS heading="Date of birth" value={dateOfBirth} />
      ) : null}

      {nationality ? (
        <SummaryFieldKS
          heading="Nationality"
          value={Country.getCountryByCode(nationality)?.name}
        />
      ) : null}

      {countryOfResidence ? (
        <SummaryFieldKS
          heading="Country of residence"
          value={Country.getCountryByCode(countryOfResidence)?.name}
        />
      ) : null}

      {currentAddress ? (
        <SummaryFieldKS
          heading="Current address"
          value={[
            `${currentAddress?.addressLine1}, ${currentAddress?.addressLine2}`,
            `${currentAddress?.city} - ${currentAddress?.postalCode}`,
            `${
              State.getStateByCodeAndCountry(
                currentAddress?.state,
                currentAddress?.country
              )?.name
            }, ${Country.getCountryByCode(currentAddress?.country)?.name}`,
          ]}
        />
      ) : null}

      {passportFile?.fileUrl ? (
        <div className="mt-2">
          <DocumentSummaryField
            onClick={() => openFile(passportFile?.id)}
            title="Passport"
            className="grid grid-cols-2"
          />
        </div>
      ) : null}

      {governmentIdFile?.fileUrl ? (
        <div className="mt-2">
          <DocumentSummaryField
            onClick={() => openFile(governmentIdFile?.id)}
            title="Government ID"
            className="grid grid-cols-2"
          />
        </div>
      ) : null}
    </div>
  );
};

export default SGDirectorSummaryField;
